import { ReactNode } from "react";
import dayjs from "dayjs";
import {
  CoachAssignmentDetails,
  CoachAssignmentDetailsList,
} from "../../model";
import { useSiteCore } from "../../../../../../../../../../hooks/useSiteCore";
import { CoachAssignmentListKeys } from "../../constants";

import {
  Column,
  MuiOptionMenu,
  MuiTable,
  RowProps,
} from "../../../../../../../../../../components";
import { coachAssignmentSiteCoreKeys } from "../../SiteCoreKeys";
import {
  MenuItemConfig,
  TableActionType,
  TableActions,
} from "../../../../../../../../../../helpers/types/common";
import {
  EMPTY_VALUE_DASH,
  MODULE_PERMISSIONS,
} from "../../../../../../../../../../helpers/constants";
import { buildActionMenuItems } from "../../../../../../../../../../helpers/utils";
import { usePermissions } from "../../../../../../../../../../hooks/usePermissions";

const {
  coachAssignment,
  coachAssignmentFunctionTableHeading,
  coachAssignmentCoachTableHeading,
  coachAssignmentAssignedDateTableHeading,
  coachAssignmentActionTableHeading,
  coachNoAssignmentsToView,
} = coachAssignmentSiteCoreKeys;

interface CoachAssignmentListProps {
  coachAssignmentList: CoachAssignmentDetailsList;
  isViewOnly?: boolean;
  dateFormat: string;
  onActionClick?: (
    action: TableActionType,
    selectedCoachAssignment: CoachAssignmentDetails,
  ) => void;
}
export const CoachAssignmentList = ({
  coachAssignmentList,
  onActionClick,
  isViewOnly = false,
  dateFormat,
}: CoachAssignmentListProps) => {
  const { commonLabels, useFetchLabel } = useSiteCore();
  const getLabel = useFetchLabel(coachAssignment);
  const emptyTableMessage = getLabel(coachNoAssignmentsToView);
  const { hasModulePermission } = usePermissions();
  const columns: Column[] = [
    {
      id: CoachAssignmentListKeys.functionName,
      label: getLabel(coachAssignmentFunctionTableHeading),
      isSortVisible: true,
    },
    {
      id: CoachAssignmentListKeys.coachFirstName,
      label: getLabel(coachAssignmentCoachTableHeading),
      isSortVisible: true,
      render: ({ coachFirstName, coachLastName }: RowProps) =>
        coachFirstName
          ? coachLastName + ", " + coachFirstName
          : EMPTY_VALUE_DASH,
    },
    {
      id: CoachAssignmentListKeys.assignmentDate,
      label: getLabel(coachAssignmentAssignedDateTableHeading),
      isSortVisible: true,
      render: (row: RowProps) =>
        row?.assignmentDate
          ? dayjs(row.assignmentDate).format(dateFormat)
          : EMPTY_VALUE_DASH,
    },
    ...(isViewOnly
      ? []
      : [
          {
            id: "action",
            label: getLabel(coachAssignmentActionTableHeading),
            render: (row: RowProps) => {
              const configs: MenuItemConfig<RowProps>[] = [
                {
                  permissionKey: MODULE_PERMISSIONS.DELETE_COACH_ASSIGNMENT,
                  create: (r) => ({
                    label: commonLabels.delete,
                    dataTestId: "delete",
                    onClick: () =>
                      onActionClick?.(
                        TableActions.delete,
                        r as CoachAssignmentDetails,
                      ),
                  }),
                },
              ];

              const menuItems = buildActionMenuItems(
                row,
                configs,
                hasModulePermission,
              );

              return menuItems ? <MuiOptionMenu menuItems={menuItems} /> : null;
            },
          },
        ]),
  ];
  return (
    <MuiTable
      pageId="coach-assignment-list-table"
      tableHeaderForm={[]}
      columns={columns}
      tableResults={
        coachAssignmentList as unknown as Record<string, ReactNode>[]
      }
      noDataInApi={coachAssignmentList.length === 0}
      emptyMessage={emptyTableMessage}
      noResultMessage={commonLabels.searchCriteria}
      priorityResults={[]}
      isFooterVisible={false}
    />
  );
};
